import React, { useState, useEffect, useRef } from 'react';
import { loadDivisionsFor } from '../utils/formationLock';
import { useNavigate } from 'react-router-dom';
import { unknownCategoriesMessage } from '../features/transactions/unknownCategories';
import api, { transactionsAPI, categoriesAPI, accountsAPI, businessesAPI, assetsAPI, vehiclesAPI, propertiesAPI } from '../services/api';
import ImportTransactionsModal from '../components/ImportTransactionsModal';
import UpdateTransactionsModal from '../components/UpdateTransactionsModal';
import ErrorBoundary from '../components/ErrorBoundary';
import SearchableSelect from '../components/SearchableSelect';
import { todayLocal } from '../utils/localDate';
import { formatApiError } from '../utils/errorMessage';
import { bulkUpdateSummary } from '../features/transactions/bulkUpdateSummary';
import { TRANSACTION_TYPE_OPTIONS, transactionTypeOptions, categoriesForType, categoryRequired } from '../features/transactions/transactionTypes';
import { PAGE_SIZE, totalFrom, fetchAllTransactions, toCsv, exportRecord, uniqueById } from '../features/transactions/transactionPages';
import TransactionPager from '../features/transactions/TransactionPager';
import { explainImportFailure } from '../features/transactions/importOutcome';
import { reconciledLockMessage, transactionErrorMessage, importSummary } from '../features/transactions/reconciledLock';
import TransactionRow from '../features/transactions/TransactionRow';
import TransactionNoteFields from '../features/transactions/TransactionNoteFields';
import SplitModal from '../features/transactions/SplitModal';
import { attachSplits } from '../features/transactions/splitParts';
import TransferPairFields, { splitPairFields } from '../features/transactions/TransferPairFields';
import DirectionField from '../features/transactions/DirectionField';
import ImportNotice from '../features/transactions/ImportNotice';

const Transactions = ({ selectedBusiness, divisionScopeIds = null }) => {
  const navigate = useNavigate();
  const isDivisionScoped = Array.isArray(divisionScopeIds) && divisionScopeIds.length > 0;
  const [transactions, setTransactions] = useState([]);
  // Import results are shown in full on the page (a browser alert cuts long text off).
  const [importNotice, setImportNotice] = useState('');
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [properties, setProperties] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formType, setFormType] = useState(null); // type picked in the edit form (shows the transfer fields)
  const [showImportModal, setShowImportModal] = useState(false); // Import new transactions modal
  const [showUpdateModal, setShowUpdateModal] = useState(false); // Update existing transactions modal
  const [showImportChoiceModal, setShowImportChoiceModal] = useState(false); // Choice between import/update
  const [splitting, setSplitting] = useState(null); // transaction being split
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState(''); // debounced; the server searches every row
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(null);
  const [exporting, setExporting] = useState(false);
  const loadSeq = useRef(0); // ignore replies to superseded page/filter requests
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    category_id: '',
    account_id: '',
    transaction_type: '',
    state_registration_id: '',
  });

  // Ensure all modals are closed on component mount
  useEffect(() => {
    setShowModal(false);
    setShowImportModal(false);
    setShowUpdateModal(false);
    setShowImportChoiceModal(false);
  }, []); // Run once on mount

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [selectedBusiness, filters, divisionScopeIds, search]);

  useEffect(() => {
    loadData();
  }, [selectedBusiness, filters, divisionScopeIds, page, search]);

  useEffect(() => {
    loadBusinessesAndSubsidiaries();
  }, [selectedBusiness, divisionScopeIds]);

  useEffect(() => {
    if (!isDivisionScoped) return;
    const allowed = divisionScopeIds.map(Number);
    setFilters((prev) => {
      const current = prev.state_registration_id;
      if (current && current !== 'parent' && allowed.includes(Number(current))) {
        return prev;
      }
      return { ...prev, state_registration_id: String(allowed[0]) };
    });
  }, [isDivisionScoped, divisionScopeIds]);

  const loadBusinessesAndSubsidiaries = async () => {
    try {
      const busRes = await businessesAPI.getAll();
      setBusinesses(busRes.data);
      
      if (selectedBusiness) {
        const location = (busRes.data || []).find((b) => String(b.id) === String(selectedBusiness));
        const registrations = await loadDivisionsFor(location, businessesAPI.getStateRegistrations);
        let list = registrations.filter(reg => reg.is_subsidiary);
        if (isDivisionScoped) {
          const allowed = new Set(divisionScopeIds.map(Number));
          list = list.filter((reg) => allowed.has(Number(reg.id)));
        }
        setSubsidiaries(list);
      } else {
        setSubsidiaries([]);
      }
    } catch (error) {
      console.error('Failed to load businesses:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const listParams = () => {
    const params = { ...filters };
    if (selectedBusiness) params.business_id = selectedBusiness;
    if (search) params.search = search; // "parent" and search are filtered server-side
    return params;
  };

  const loadData = async () => {
    try {
      const params = { ...listParams(), limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      const seq = ++loadSeq.current;
      
      const [transRes, catRes, accRes, assetRes, vehicleRes, propertyRes] = await Promise.all([
        transactionsAPI.getAll(params),
        categoriesAPI.getAll(),
        accountsAPI.getAll(selectedBusiness ? { business_id: selectedBusiness } : {}),
        assetsAPI.getAll(selectedBusiness ? { business_id: selectedBusiness } : {}).catch(() => ({ data: [] })),
        vehiclesAPI.getAll(selectedBusiness ? { business_id: selectedBusiness } : {}).catch(() => ({ data: [] })),
        propertiesAPI.getAll(selectedBusiness ? { business_id: selectedBusiness } : {}).catch(() => ({ data: [] })),
      ]);
      if (seq !== loadSeq.current) return;
      
      let filteredTransactions = uniqueById(transRes.data);
      filteredTransactions = await attachSplits(filteredTransactions, transactionsAPI.splitsFor).catch(() => filteredTransactions);
      if (seq !== loadSeq.current) return;
      setTransactions(filteredTransactions);
      setTotalCount(totalFrom(transRes));
      setCategories(catRes.data);
      setAccounts(accRes.data);
      setAssets(assetRes.data || []);
      setVehicles(vehicleRes.data || []);
      setProperties(propertyRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const [data, pair] = splitPairFields(Object.fromEntries(formData), editingTransaction?.id || 'new');
    if (categoryRequired(data, editingTransaction)) return alert('Choose a category (transfers and adjustments can go without one).');
    
    try {
      const saved = editingTransaction
        ? await transactionsAPI.update(editingTransaction.id, data)
        : await transactionsAPI.create(data);
      if (pair) {
        // Link the other side of the transfer, or create it in a non-bank account.
        const linked = (await api.post('/transfer-pairs', { ...pair, transaction_id: saved?.data?.id || editingTransaction?.id })).data;
        alert(linked.action === 'created'
          ? `Created the other side of this transfer in ${linked.account_name}.`
          : `Paired with row #${linked.paired_transaction_id} in ${linked.account_name}.`);
      }
      setShowModal(false);
      setFormType(null);
      setEditingTransaction(null);
      loadData();
    } catch (error) {
      console.error('Failed to save transaction:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(transactionErrorMessage(error, 'Could not save the transaction'));
      loadData();
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        await transactionsAPI.delete(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete transaction:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
        alert(transactionErrorMessage(error, 'Could not delete the transaction'));
      }
    }
  };

  const handleBulkImport = async (transactions) => {
    try {
      // Add business_id to each transaction if a business is selected
      const transactionsWithBusiness = transactions.map(t => ({
        ...t,
        business_id: selectedBusiness || null
      }));
      
      const response = await transactionsAPI.bulkImport(transactionsWithBusiness);
      setImportNotice(importSummary(response.data || {}));
      try {
        await loadData();
      } catch (loadError) {
        console.error('Failed to reload after import:', loadError?.response?.status || loadError?.code || 'no response', loadError?.response?.data?.code || '');
      }
      return true;
    } catch (error) {
      console.error('Failed to import transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      const unknownText = unknownCategoriesMessage(error);
      if (unknownText) { alert(unknownText); return false; }
      if (reconciledLockMessage(error)) { setImportNotice(reconciledLockMessage(error)); return false; }
      const { landed, message } = await explainImportFailure(error, transactions, transactionsAPI.checkDuplicates, formatApiError);
      alert(message);
      try {
        await loadData();
      } catch (loadError) {
        console.error('Failed to reload after import error:', loadError?.response?.status || loadError?.code || 'no response', loadError?.response?.data?.code || '');
      }
      return landed;
    }
  };

  const handleDeleteImported = async () => {
    const accountId = filters.account_id;
    if (!accountId) {
      alert('Choose an account in the filters first.');
      return;
    }
    const account = accounts.find((a) => String(a.id) === String(accountId));
    const accountName = account?.account_name || 'this account';
    if (!window.confirm(
      `Delete every imported transaction in ${accountName}?\n\nOnly rows from Import from Excel / bank import are removed. Hand-entered rows stay. This cannot be undone.`
    )) {
      return;
    }
    try {
      const response = await transactionsAPI.deleteImported(
        accountId,
        selectedBusiness || account?.business_id || undefined
      );
      const deleted = Number(response.data?.deleted) || 0;
      alert(deleted === 1
        ? 'Deleted 1 imported transaction.'
        : `Deleted ${deleted} imported transactions.`);
      await loadData();
    } catch (error) {
      console.error('Failed to delete imported transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(formatApiError(error, 'Failed to delete imported transactions'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const filteredTransactions = transactions;

  // Export every matching transaction (all pages, not just the one on screen)
  const handleExportToExcel = async () => {
    setExporting(true);
    let rows;
    try {
      rows = await attachSplits(await fetchAllTransactions(transactionsAPI.getAll, listParams()), transactionsAPI.splitsFor);
    } catch (error) {
      setExporting(false);
      alert(formatApiError(error, 'Could not export transactions'));
      return;
    }
    setExporting(false);
    if (rows.length === 0) {
      alert('No transactions to export');
      return;
    }

    const exportData = rows.map(exportRecord);
    const csv = toCsv(exportData);

    // Download file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `transactions_${todayLocal()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Handle bulk update from Excel
  const handleBulkUpdate = async (transactions) => {
    try {
      const response = await transactionsAPI.bulkUpdate(transactions);
      
      if (response.data.success) {
        alert(bulkUpdateSummary(response.data.results)); // lists rows refused by the reconciled-month guard
        loadData(); // Reload transactions
      }
    } catch (error) {
      console.error('Failed to update transactions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      alert(unknownCategoriesMessage(error) || formatApiError(error, 'Failed to update transactions'));
    }
  };

  // Reload categories after adding new ones
  const reloadCategories = async () => {
    try {
      const catRes = await categoriesAPI.getAll();
      setCategories(catRes.data);
    } catch (error) {
      console.error('Failed to reload categories:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  return (
    <div className="space-y-6">
      <ImportNotice text={importNotice} onClose={() => setImportNotice('')} />
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Transactions</h2>
          <p className="text-gray-600 mt-1">Manage your income and expenses</p>
        </div>
        <div className="flex items-center gap-2">
          <ErrorBoundary>
            <button
              onClick={() => navigate('/categories')}
              className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition"
            >
              🏷️ Manage Categories
            </button>
          </ErrorBoundary>
          <ErrorBoundary>
            <button
              onClick={() => setShowImportChoiceModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
            >
              📊 Import from Excel
            </button>
          </ErrorBoundary>
          <ErrorBoundary>
            <button
              onClick={handleExportToExcel}
              disabled={exporting}
              className="disabled:opacity-60 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition"
            >
              {exporting ? 'Exporting…' : '📥 Export to Excel'}
            </button>
          </ErrorBoundary>
          <ErrorBoundary>
            <button
              type="button"
              onClick={handleDeleteImported}
              disabled={!filters.account_id}
              title={filters.account_id ? 'Delete imported rows for the filtered account' : 'Choose an account filter first'}
              className="bg-red-700 text-white px-4 py-2 rounded-lg hover:bg-red-800 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Delete imported
            </button>
          </ErrorBoundary>
          <ErrorBoundary>
            <button
              onClick={() => { setShowModal(true); setEditingTransaction(null); setFormType(null); }}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              + Add Transaction
            </button>
          </ErrorBoundary>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        {/* Search Field */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="🔍 Search transactions (date, description, business, category, account, reference, amount, type...)"
              className="w-full border rounded-lg px-4 py-3 pl-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        
        {/* Date and Category Filters */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="border rounded px-3 py-2"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="border rounded px-3 py-2"
            placeholder="End Date"
          />
          <SearchableSelect
            value={filters.account_id}
            onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}
            options={[
              { id: '', account_name: 'All Accounts' },
              ...accounts
            ]}
            placeholder="All Accounts"
            getOptionLabel={(option) => option.account_name || option.label}
            getOptionValue={(option) => option.id !== undefined ? String(option.id) : option.value || ''}
            className="border rounded"
          />
          <SearchableSelect
            value={filters.category_id}
            onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}
            options={[
              { id: '', name: 'All Categories' },
              ...categories
            ]}
            placeholder="All Categories"
            getOptionLabel={(option) => option.name || option.label}
            getOptionValue={(option) => option.id !== undefined ? String(option.id) : option.value || ''}
            className="border rounded"
          />
          <SearchableSelect
            value={filters.transaction_type}
            onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
            options={[
              { value: '', label: 'All Types' },
              ...TRANSACTION_TYPE_OPTIONS
            ]}
            placeholder="All Types"
            getOptionLabel={(option) => option.label || option.name}
            getOptionValue={(option) => option.value || option.id || ''}
            className="border rounded"
          />
          <SearchableSelect
            value={filters.state_registration_id}
            onChange={(e) => setFilters({ ...filters, state_registration_id: e.target.value })}
            options={isDivisionScoped
              ? subsidiaries.map(sub => ({
                  id: sub.id,
                  label: `${sub.subsidiary_name} (${sub.state})`
                }))
              : [
                  { id: '', label: 'All Operations' },
                  { id: 'parent', label: 'Parent Business Only' },
                  ...subsidiaries.map(sub => ({
                    id: sub.id,
                    label: `${sub.subsidiary_name} (${sub.state})`
                  }))
                ]}
            placeholder={isDivisionScoped ? 'Your division' : 'All Operations'}
            disabled={!selectedBusiness || (isDivisionScoped && subsidiaries.length <= 1)}
            getOptionLabel={(option) => option.label || option.subsidiary_name || option.name}
            getOptionValue={(option) => {
              if (option.id === 'parent') return 'parent';
              return option.id !== undefined ? String(option.id) : option.value || '';
            }}
            className="border rounded"
          />
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business/Subsidiary</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTransactions.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                  {searchQuery ? `No transactions found matching "${searchQuery}"` : 'No transactions found'}
                </td>
              </tr>
            ) : (
              filteredTransactions.map((transaction) => (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  formatCurrency={formatCurrency}
                  onEdit={(t) => { setEditingTransaction(t); setFormType(null); setShowModal(true); }}
                  onDelete={handleDelete}
                  onSplit={setSplitting}
                  onSetDirection={(t, d) => transactionsAPI.setDirection(t.id, d).then(loadData)
                    .catch((err) => alert(formatApiError(err, 'Could not save the direction')))}
                />
            )))}
          </tbody>
        </table>
      </div>
      <TransactionPager page={page} total={totalCount} onPage={setPage} />

      {splitting && (
        <SplitModal transaction={splitting} categories={categories} accounts={accounts} api={transactionsAPI}
          onClose={() => setSplitting(null)} onSaved={() => { setSplitting(null); loadData(); }} />
      )}
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingTransaction ? 'Edit' : 'Add'} Transaction</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="business_id" value={selectedBusiness || editingTransaction?.business_id || ''} />
              
              {/* Subsidiary Selector */}
              {selectedBusiness && subsidiaries.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                  <label className="block text-sm font-medium mb-2">Assign to Business/Subsidiary</label>
                  <select
                    name="state_registration_id"
                    defaultValue={
                      editingTransaction?.state_registration_id
                      || (isDivisionScoped ? divisionScopeIds[0] : '')
                    }
                    required={isDivisionScoped}
                    className="w-full border rounded px-3 py-2"
                  >
                    {!isDivisionScoped && (
                      <option value="">
                        {businesses.find(b => b.id === parseInt(selectedBusiness))?.name} (Parent Business)
                      </option>
                    )}
                    {subsidiaries.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.subsidiary_name} ({sub.state}) - {sub.industry}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-600 mt-1">
                    Select which division/subsidiary this transaction belongs to
                  </p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  name="transaction_date"
                  defaultValue={editingTransaction?.transaction_date?.split('T')[0] || todayLocal()}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  name="description"
                  defaultValue={editingTransaction?.description || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  name="transaction_type"
                  defaultValue={editingTransaction?.transaction_type || 'expense'}
                  onChange={(e) => setFormType(e.target.value)}
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  {transactionTypeOptions(editingTransaction?.transaction_type).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  name="amount"
                  step="0.01"
                  defaultValue={editingTransaction?.transaction_type === 'transfer' && editingTransaction?.amount != null ? Math.abs(Number(editingTransaction.amount)) : (editingTransaction?.amount || '')}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  name="category_id"
                  defaultValue={editingTransaction?.category_id || ''}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">{editingTransaction && !editingTransaction.category_id ? "Uncategorized (choose one later)" : "Select Category (optional for transfers and adjustments)"}</option>
                  {categoriesForType(categories, formType || editingTransaction?.transaction_type || 'expense', editingTransaction?.category_id).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Account</label>
                <select
                  name="account_id"
                  defaultValue={editingTransaction?.account_id || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Account</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                  ))}
                </select>
              </div>

              <DirectionField transaction={editingTransaction} type={formType || editingTransaction?.transaction_type || 'expense'} />

              <TransferPairFields transaction={editingTransaction} accounts={accounts} type={formType || editingTransaction?.transaction_type || 'expense'} />

              <div>
                <label className="block text-sm font-medium mb-1">Asset</label>
                <select name="asset_id" defaultValue={editingTransaction?.asset_id || ''} className="w-full border rounded px-3 py-2">
                  <option value="">No asset</option>
                  {assets.map((asset) => (
                    <option key={asset.id} value={asset.id}>{asset.asset_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vehicle</label>
                <select name="vehicle_id" defaultValue={editingTransaction?.vehicle_id || ''} className="w-full border rounded px-3 py-2">
                  <option value="">No vehicle</option>
                  {vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>{vehicle.vehicle_name || 'Vehicle'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Property</label>
                <select name="property_id" defaultValue={editingTransaction?.property_id || ''} className="w-full border rounded px-3 py-2">
                  <option value="">No property</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>{property.property_name || 'Property'}</option>
                  ))}
                </select>
              </div>
              
              <TransactionNoteFields transaction={editingTransaction} />
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingTransaction(null); }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingTransaction ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Choice Modal */}
      {showImportChoiceModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            // Close modal if clicking the backdrop
            if (e.target === e.currentTarget) {
              setShowImportChoiceModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Import from Excel</h3>
            <p className="text-gray-600 mb-6">What would you like to do?</p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowImportChoiceModal(false);
                  setShowImportModal(true);
                }}
                className="w-full bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition text-left"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">➕</span>
                  <div>
                    <div className="font-semibold">Import New Transactions</div>
                    <div className="text-sm text-green-100">Add new transactions from Excel/CSV file</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  setShowImportChoiceModal(false);
                  setShowUpdateModal(true);
                }}
                className="w-full bg-orange-600 text-white px-6 py-4 rounded-lg hover:bg-orange-700 transition text-left"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">🔄</span>
                  <div>
                    <div className="font-semibold">Update Existing Transactions</div>
                    <div className="text-sm text-orange-100">Modify existing transactions from Excel/CSV file</div>
                  </div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowImportChoiceModal(false)}
              className="w-full mt-4 px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <ErrorBoundary>
        <ImportTransactionsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleBulkImport}
          accounts={accounts}
          categories={categories}
        />
      </ErrorBoundary>

      {/* Bulk Update Modal */}
      <ErrorBoundary>
        <UpdateTransactionsModal
          isOpen={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          onUpdate={handleBulkUpdate}
          categories={categories}
          onCategoriesUpdate={reloadCategories}
        />
      </ErrorBoundary>
    </div>
  );
};

export default Transactions;
